import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SortDownArrow } from "../../assets/svg";
import Action from "../../components/common/Action";
import Toggles from "../../components/common/Toggles";
import Breadcrumb from "../../components/common/BreadCrumb";
import DebounceSearch from "../../components/common/DebounceSearch";
import CustomTable from "../../components/customTable/CustomTable";
import RippleLoader from "../../components/loader/RippleLoader";
import { AddButton } from "../../components/micro/input";
import ConfirmationModel from "../../components/common/ConfirmationModel";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteStaff,
  getStaffsList,
  resetPassword,
  updateStaffStatus,
} from "../../redux/staffs/slice";
import { getAllRoleList } from "../../redux/accounts/slice";
import ReactSelect from "react-select";

const StaffAccounts = () => {
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("active");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showConfirmPasswordPopup, setShowConfirmPasswordPopup] =
    useState(false);
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [allRoleListArray, setAllRoleListArray] = useState();
  const [selectedRole, setSelectedRole] = useState(null);
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmUnarchive, setConfirmUnarchive] = useState(false);
  const [staffIdToArchive, setStaffIdToArchive] = useState(null);
  const [staffIdToUnarchive, setStaffIdToUnarchive] = useState(null);
  const [staffToResetPassword, setStaffToResetPassword] = useState(null);
  const [staffIdToDelete, setStaffIdToDelete] = useState(null);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // selector
  const { staffsList, staffsListLoading, allRoleList, allRoleLoading } =
    useSelector((store) => ({
      staffsList: store?.staffsData?.getStaffsListStatus?.data,
      staffsListLoading: store?.staffsData?.loading,
      // all RoleL ist
      allRoleList: store?.accountsData?.getAllRoleListStatus?.data,
      allRoleLoading: store?.accountsData?.getAllRoleListStatus?.loading,
    }));

  useEffect(() => {
    dispatch(
      getStaffsList({
        search: searchValue,
        pageCount: pageCount,
        pageLimit: pageLimit,
        status: activeTab === "archived" ? "archive" : activeTab,
        role: selectedRole?.id,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
      })
    );
  }, [
    dispatch,
    searchValue,
    pageCount,
    pageLimit,
    activeTab,
    selectedRole,
    sortBy,
    sortDirection,
  ]);

  useEffect(() => {
    dispatch(getAllRoleList());
  }, []);

  useEffect(() => {
    if (allRoleList?.data) {
      let data = allRoleList.data?.map((x) => ({
        ...x,
        label: x.title,
        value: x,
      }));
      setAllRoleListArray(data);
    }
  }, [allRoleList]);

  const updateStatus = (element, status, isActive) => {
    dispatch(
      updateStaffStatus({
        id: element?.id,
        data: {
          status: status,
          is_active: isActive,
        },
      })
    ).then((res) => {
      if (res.type === "updateStaffStatus/fulfilled") {
        dispatch(
          getStaffsList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            status: activeTab === "archived" ? "archive" : activeTab,
            role: selectedRole?.id,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
    });
  };

  const handleArchive = () => {
    const staffToArchive = staffsList?.data?.data?.find(
      (staff) => staff.id === staffIdToArchive
    );

    if (staffToArchive) {
      updateStatus(staffToArchive, "archive", false);
    }
    setConfirmArchive(false);
  };

  const handleUnarchive = () => {
    const staffToUnarchive = staffsList?.data?.data?.find(
      (staff) => staff.id === staffIdToUnarchive
    );

    if (staffToUnarchive) {
      updateStatus(staffToUnarchive, "active", true);
    }
    setConfirmUnarchive(false);
  };

  const sendResetPassword = (element) => {
    if (!element) return;
    dispatch(resetPassword(element?.id)).then((res) => {
      setShowConfirmPasswordPopup(false);
    });
  };

  const deleteStaffId = () => {
    setIsLoading(true);
    if (!staffIdToDelete) return;
    dispatch(deleteStaff(staffIdToDelete)).then((res) => {
      if (res.type === "deleteStaff/fulfilled") {
        dispatch(
          getStaffsList({
            search: searchValue,
            pageCount: pageCount,
            pageLimit: pageLimit,
            status: activeTab === "archived" ? "archive" : activeTab,
            role: selectedRole?.id,
            sort_by: sortBy,
            sort_direction: sortDirection === "asc" ? "asc" : "desc",
          })
        );
      }
      setIsLoading(false);
      setConfirmDelete(false);
    });
  };

  const handleToggle = (element) => {
    const newStatus = element.status === "active" ? "inactive" : "active";
    const isActive = newStatus === "active";

    updateStatus(element, newStatus, isActive);
  };

  // Table Headers
  const ColumnHeaders = () => (
    <>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        ID
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Name</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "name" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("role");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Role </span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "role" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Mobile
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Email
      </th>
      {activeTab === "active" && (
        <th
          scope="col"
          className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
        >
          <div className="flex items-center">
            <span>Status</span>
          </div>
        </th>
      )}
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        Action
      </th>
    </>
  );

  // Table Rows
  const DataRows = () => (
    <>
      {(staffsList?.data?.data || [])?.map((element, index) => (
        <tr
          key={element.id}
          className="text-sm shadow-sm text-mgrey hover:bg-lightprimaryorange font-Proxima"
        >
          <td className="px-5 py-3 truncate">{element?.id}</td>
          <td className="px-5 py-3 truncate">
            {element?.full_name
              ? element?.full_name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3 truncate">
            {element?.role
              ? element?.role
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())
              : "-"}
          </td>
          <td className="px-5 py-3 truncate">{element?.phone}</td>
          <td className="px-5 py-3 truncate">{element?.email}</td>
          {activeTab === "active" && (
            <td className="px-5 py-3 truncate">
              <Toggles
                checked={element?.status === "active"}
                toggleFor={element?.id}
                onChange={() => handleToggle(element)}
              />
            </td>
          )}
          <td className="flex px-5 py-3">
            {activeTab === "active" ? (
              <Action
                optionList={
                  <>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() =>
                        navigate("/create-edit-staff-account", {
                          state: { staff: element, mode: "edit" },
                        })
                      }
                    >
                      View/Edit
                    </li>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setConfirmArchive(true);
                        setStaffIdToArchive(element?.id);
                      }}
                    >
                      Archive
                    </li>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setShowConfirmPasswordPopup(true);
                        setStaffToResetPassword(element);
                      }}
                    >
                      Reset Password
                    </li>
                  </>
                }
              />
            ) : (
              <Action
                optionList={
                  <>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setConfirmUnarchive(true);
                        setStaffIdToUnarchive(element?.id);
                      }}
                    >
                      Unarchive
                    </li>
                    <li
                      className="flex gap-2 items-center px-6 py-2 bg-white hover:bg-hoverTheme transition duration-300 cursor-pointer"
                      onClick={() => {
                        setConfirmDelete(true);
                        setStaffIdToDelete(element?.id);
                      }}
                    >
                      Delete
                    </li>
                  </>
                }
              />
            )}
          </td>
        </tr>
      ))}
    </>
  );

  return (
    <>
      <div className="space-y-5">
        <div className="flex justify-between items-start">
          <div>
            <h6 className="font-Montserrat font-medium text-xl mb-3">
              {activeTab === "active"
                ? "Staff Account"
                : "Archived Staff Account"}
            </h6>
            <Breadcrumb
              breadCrumbParent={"Home"}
              breadCrumbActive={
                activeTab === "active"
                  ? "Staff Account"
                  : "Archived Staff Account"
              }
              breadCrumbParent2={"Accounts"}
              breadCrumbTitleKey={"/"}
              classes=""
            />
          </div>
          {activeTab === "active" ? (
            <AddButton
              value="Add Staff"
              onClick={() =>
                navigate("/create-edit-staff-account", {
                  state: { mode: "create" },
                })
              }
            />
          ) : (
            ""
          )}
        </div>
        <div className="flex justify-end my-8">
          {["active", "archived"].map((tab, index) => (
            <button
              key={tab}
              className={`transition font-Proxima uppercase duration-300 text-sm font-normal px-4 py-1.5
        ${index === 0 ? "!rounded-l !rounded-r-none" : ""}
        ${index === 1 ? "!rounded-r !rounded-l-none" : ""}
        ${
          activeTab === tab
            ? "bg-themeBtn text-white shadow-[0_4px_6px_rgba(0,0,0,0.3)]"
            : "bg-disabletheme text-white shadow-[0_4px_6px_rgba(0,0,0,0.2)]"
        }`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>
        <div className="flex items-center space-x-3">
          <ReactSelect
            className={"w-full max-w-52 text-black p-0 font-Proxima"}
            id="role"
            placeholder="Role"
            options={allRoleListArray}
            isLoading={allRoleLoading}
            isClearable={true}
            getOptionValue={(selectedRole) => selectedRole.value}
            getOptionLabel={(selectedRole) => selectedRole?.label}
            value={selectedRole}
            onChange={(e) => {
              setSelectedRole(e);
            }}
            styles={{
              control: (base) => ({
                ...base,
                border: `1px solid #0000003B`,
                borderColor: selectedRole ? "#F15B29" : "#0000003B",
                borderRadius: "0.3rem",
                boxShadow: "none",
                "&:hover": {
                  border: "1px solid #0000003B",
                },
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected ? "#F15B29" : "white",
                "&:active": {
                  backgroundColor: state.isSelected
                    ? "#F15B29"
                    : "rgba(0, 0, 0, 0.04)",
                },
              }),
            }}
          />
          <DebounceSearch
            search={searchValue}
            handleChange={setSearchValue}
            placeholder={"Search..."}
          />
        </div>
        {staffsListLoading ? (
          <RippleLoader />
        ) : (
          <div className="w-full bg-white shadow rounded-lg">
            <CustomTable
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={staffsList?.data}
              overflowVisibleLg={true}
              // loader
              loading={false}
              showPagination={true}
              // showLimit
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              // paginationData
              total={staffsList?.data?.total}
              current={pageCount}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(staffsList?.data?.total / pageLimit)}
            />
          </div>
        )}
      </div>
      {confirmArchive && (
        <div>
          <ConfirmationModel
            confirmText={"Archive"}
            onClose={() => {
              setConfirmArchive(false);
            }}
            onConfirm={handleArchive}
          />
        </div>
      )}

      {confirmUnarchive && (
        <div>
          <ConfirmationModel
            confirmText={"Unarchive"}
            onClose={() => {
              setConfirmUnarchive(false);
            }}
            onConfirm={handleUnarchive}
          />
        </div>
      )}
      {showConfirmPasswordPopup && (
        <div>
          <ConfirmationModel
            confirmText={"Send Password Reset Link"}
            onClose={() => setShowConfirmPasswordPopup(false)}
            onConfirm={() => {
              sendResetPassword(staffToResetPassword);
              setShowConfirmPasswordPopup(false);
            }}
            width="w-[420px]"
          />
        </div>
      )}

      {confirmDelete && (
        <div>
          <ConfirmationModel
            confirmText={"Delete"}
            onClose={() => {
              setConfirmDelete(false);
            }}
            onConfirm={deleteStaffId}
            isLoading={isLoading}
          />
        </div>
      )}
    </>
  );
};

export default StaffAccounts;
